// persist.js — where Lift Log keeps its data on the device.
//
// Durable store: IndexedDB `liftlog_state` (Dexie)
//   sessions  one record per COMPLETED workout (key: id)
//   kv        'doc'  everything else in the app state (programs, exercises,
//                    prefs, PRs, body weights, …) — written only when it changes
//             'live' the workout in progress (or null)
//             'head' { rev, at } — bumped by every write
//             'rollback.v1'  the original localStorage data, kept after the
//                    one-time migration (dropped once STORAGE_V moves on)
//             'preRestore'   what was on the device before the last JSON restore
//             'preImport'    what IndexedDB held before an external 'liftlog.v1' replaced it
//
// The in-memory React state stays the source of truth for the UI. Writes are
// debounced and batched into one IndexedDB transaction that only touches
// records whose object identity changed, so logging a set writes the live
// workout and nothing else.
//
// Fast start: localStorage 'liftlog.v1' keeps a MIRROR of the state (written
// after IndexedDB commits, at most MIRROR_MAX characters — a big history
// mirrors only its recent workouts and is marked partial; mid-workout only
// while it is small). The app renders from it synchronously, then hydrate()
// reconciles with IndexedDB. 'liftlog.v1.sig' = { rev, len, h, partial } identifies our
// own mirror. 'liftlog.live' holds the live workout, written on every change
// (a few KB), so an app kill right after a set loses nothing.
//
// Test / dev seam (documented): any 'liftlog.v1' that is NOT our mirror (no
// or non-matching signature — the pre-IndexedDB app, a seeding script, an
// edited copy) is imported on load: it replaces what IndexedDB holds (the old
// contents are kept as 'preImport') and becomes the new mirror. Scripts can
// also call window.__liftlog.seed(state) (any size) and reload.

import Dexie from 'dexie';

export const LS_KEY = 'liftlog.v1';
export const SIG_KEY = 'liftlog.v1.sig';
export const LIVE_KEY = 'liftlog.live';
export const IDB_FLAG = 'liftlog.idb';
export const UNDO_KEY = 'liftlog.undoRestore';
export const STORAGE_V = 1;
const DB_NAME = 'liftlog_state';
const MIRROR_MAX = 1_500_000;      // chars of localStorage the mirror may use
const MIRROR_RECENT = 60;          // workouts kept in a partial mirror
const LIVE_MIRROR_MAX = 400_000;   // mid-workout, the mirror is rewritten only below this size
const DEBOUNCE_MS = 400;
const MAX_WAIT_MS = 2000;
const EPHEMERAL = new Set(['toasts', 'hydrated', 'sessions']);

// ───────────────────────────── helpers ─────────────────────────────

const ls = {
  get(k) { try { return localStorage.getItem(k); } catch (_) { return null; } },
  set(k, v) { localStorage.setItem(k, v); },
  del(k) { try { localStorage.removeItem(k); } catch (_) { /* ignore */ } },
};
const parse = (s) => { try { return s ? JSON.parse(s) : null; } catch (_) { return null; } };
export function strHash(str) {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761); h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
const isQuota = (e) => !!e && (e.name === 'QuotaExceededError' || e.code === 22 || (e.inner && e.inner.name === 'QuotaExceededError') || /quota/i.test(e.message || ''));
const liveOf = (s) => (s && s.activeSessionId ? (s.sessions || []).find((x) => x && x.id === s.activeSessionId && !x.completedAt) || null : null);
/** The state minus completed sessions and ephemeral fields (the 'doc' record). */
export function docOf(s) {
  const out = {};
  for (const k of Object.keys(s || {})) if (!EPHEMERAL.has(k)) out[k] = s[k];
  return out;
}
/** The persistable state (what backups and the mirror hold). */
export function persistable(s) {
  const { toasts: _t, hydrated: _h, ...rest } = s || {};
  return rest;
}

// ───────────────────────────── IndexedDB ─────────────────────────────

let _db = null;
let _noIdb = false;
function db() {
  if (_db) return _db;
  if (_noIdb || typeof indexedDB === 'undefined') return null;
  _db = new Dexie(DB_NAME);
  _db.version(1).stores({ sessions: 'id', kv: 'k' });
  return _db;
}
export const idbAvailable = () => !!db();

async function kvGet(k) { const d = db(); if (!d) return null; const r = await d.kv.get(k); return r || null; }
export async function kvPut(k, v) { const d = db(); if (!d) return; await d.kv.put({ k, ...v }); }
export async function kvDel(k) { const d = db(); if (!d) return; await d.kv.delete(k); }

/** Everything in IndexedDB: { head, doc, live, sessions } (sessions only when asked). */
export async function readAll({ sessions = true } = {}) {
  const d = db(); if (!d) return null;
  const [head, doc, live] = await Promise.all([kvGet('head'), kvGet('doc'), kvGet('live')]);
  // Workouts are read in pages (keyset on id): each page is its own task, so
  // thousands of records never deserialize in one long main-thread task.
  let list = null;
  if (sessions) {
    list = [];
    let last = null;
    for (;;) {
      const page = await (last == null ? d.sessions.orderBy(':id') : d.sessions.where(':id').above(last)).limit(250).toArray();
      if (!page.length) break;
      for (const x of page) list.push(x);
      last = page[page.length - 1].id;
      if (page.length < 250) break;
    }
  }
  return { head, doc, live, sessions: list };
}

/** Replace the whole IndexedDB copy with `state` (migration, seeding, re-persist). */
export async function writeAll(state, rev) {
  const d = db(); if (!d) throw new Error('IndexedDB unavailable');
  const s = persistable(state);
  const done = (s.sessions || []).filter((x) => x && x.id != null && x.completedAt);
  const live = liveOf(s);
  await d.transaction('rw', d.sessions, d.kv, async () => {
    await d.sessions.clear();
    if (done.length) await d.sessions.bulkPut(done);
    await d.kv.put({ k: 'doc', rev, at: Date.now(), data: docOf(s) });
    await d.kv.put({ k: 'live', rev, at: Date.now(), data: live });
    await d.kv.put({ k: 'head', rev, at: Date.now(), v: STORAGE_V });
  });
  try { ls.set(IDB_FLAG, '1'); } catch (_) { /* quota: harmless */ }
  return { sessions: done.length };
}

/** Read back and compare (ids and set counts) — the migration only deletes its source after this passes. */
export async function verifyAll(state, rev) {
  const got = await readAll();
  if (!got || !got.head || got.head.rev !== rev || !got.doc) return false;
  const want = (state.sessions || []).filter((x) => x && x.id != null && x.completedAt);
  if (got.sessions.length !== new Set(want.map((x) => x.id)).size) return false;
  const byId = new Map(got.sessions.map((x) => [x.id, x]));
  for (const x of want) { const y = byId.get(x.id); if (!y || (y.sets || []).length !== (x.sets || []).length || y.completedAt !== x.completedAt) return false; }
  const d = got.doc.data || {};
  return (d.programs || []).length === (state.programs || []).length && (d.exercises || []).length === (state.exercises || []).length;
}

export async function clearAll() {
  const d = db(); if (!d) return;
  await d.transaction('rw', d.sessions, d.kv, async () => { await d.sessions.clear(); await d.kv.clear(); });
}

// ───────────────────────────── boot (synchronous) ─────────────────────────────

/**
 * What to render first. → { data, source, rev, partial, raw, live, corrupt }
 *   source 'mirror'   our own mirror (fast path)
 *          'external' a 'liftlog.v1' we did not write (pre-IndexedDB data / seed) → imported by hydrate
 *          'idb'      no mirror, but IndexedDB has data → wait for hydrate before rendering
 *          'none'     fresh install
 */
export function bootRead() {
  const raw = ls.get(LS_KEY);
  const live = parse(ls.get(LIVE_KEY));
  if (raw) {
    let data = null;
    try { data = JSON.parse(raw); } catch (_) { return { source: 'corrupt', raw, live }; }
    if (!data || typeof data !== 'object' || Array.isArray(data)) return { source: 'corrupt', raw, live };
    const sig = parse(ls.get(SIG_KEY));
    const ours = !!(sig && sig.len === raw.length && sig.h === strHash(raw));
    return { data, raw, source: ours ? 'mirror' : 'external', rev: ours ? sig.rev || 0 : 0, partial: !!(ours && sig.partial), live };
  }
  return { source: ls.get(IDB_FLAG) === '1' ? 'idb' : 'none', live };
}

// ───────────────────────────── cross-tab ─────────────────────────────

const TAB = Math.random().toString(36).slice(2);
let chan = null;
function channel(onMsg) {
  if (typeof BroadcastChannel !== 'undefined') {
    chan = new BroadcastChannel('liftlog');
    chan.onmessage = (e) => onMsg(e.data);
    return (m) => { try { chan.postMessage(m); } catch (_) { /* closed */ } };
  }
  // Old Safari: the storage event carries the same message.
  if (typeof window !== 'undefined') window.addEventListener('storage', (e) => { if (e.key === 'liftlog.bc' && e.newValue) { const m = parse(e.newValue); if (m) onMsg(m); } });
  return (m) => { try { ls.set('liftlog.bc', JSON.stringify(m)); } catch (_) { /* ignore */ } };
}

// ───────────────────────────── the persister ─────────────────────────────

/**
 * createPersister({ getState, onStorageFull, onSaved, onRemote })
 *   getState()          latest in-memory state
 *   onStorageFull(bool) a write failed for lack of space (true) / writes work again (false)
 *   onRemote(patch)     another tab saved: patch = { doc?, put: [sessions], del: [ids], live?, hasLive }
 */
export function createPersister(host) {
  // Identities of what is on disk.
  const P = { doc: null, sessions: new Map(), live: null, liveSet: false };
  let rev = 0;
  let hydrated = false;
  let timer = null, firstDirty = 0, busy = null, again = false;
  let lastMirror = null;      // the exact text of our last mirror write (to spot external writes)
  let mirrorBlocked = false;  // someone else wrote 'liftlog.v1' during this session — never overwrite it
  let avgLen = 4000;          // mirror chars per workout (size estimate before stringifying)
  let full = false;
  let lastLiveJSON = null;
  let frozen = false;         // window.__liftlog.seed() replaced the data on disk: this tab writes nothing more
  const post = channel(onMessage);
  if (typeof window !== 'undefined') {
    // Leaving the page: commit what is pending (the live workout is already in localStorage).
    window.addEventListener('pagehide', () => { if (timer || again) flush(); });
    document.addEventListener('visibilitychange', () => { if (document.hidden && (timer || again)) flush(); });
  }

  function baseline(state) {
    const s = state || host.getState();
    P.doc = docOf(s);
    P.sessions = new Map();
    for (const x of s.sessions || []) if (x && x.completedAt && x.id != null) P.sessions.set(x.id, x);
    P.live = liveOf(s); P.liveSet = true;
  }

  // ── live workout: written synchronously to localStorage on every change ──
  function writeLiveKey(live) {
    if (!live) { if (lastLiveJSON !== '') { ls.del(LIVE_KEY); lastLiveJSON = ''; } return; }
    const json = JSON.stringify({ id: live.id, at: Date.now(), session: live });
    if (json === lastLiveJSON) return;
    try { ls.set(LIVE_KEY, json); lastLiveJSON = json; } catch (_) { /* quota: IndexedDB still gets it */ }
  }

  function schedule(state) {
    if (frozen) return;
    const live = liveOf(state);
    if (live !== P.live || !P.liveSet) writeLiveKey(live);
    if (!hydrated) return; // hydrate() writes everything once it knows what is on disk
    const now = Date.now();
    if (!timer) firstDirty = now;
    clearTimeout(timer);
    const wait = Math.max(0, Math.min(DEBOUNCE_MS, MAX_WAIT_MS - (now - firstDirty)));
    timer = setTimeout(() => { timer = null; flush(); }, wait);
  }

  function diff(s) {
    const doc = docOf(s);
    let docChanged = !P.doc;
    if (P.doc) {
      const keys = new Set([...Object.keys(doc), ...Object.keys(P.doc)]);
      for (const k of keys) if (doc[k] !== P.doc[k]) { docChanged = true; break; }
    }
    const put = [], seen = new Set();
    for (const x of s.sessions || []) {
      if (!x || !x.completedAt || x.id == null) continue;
      seen.add(x.id);
      if (P.sessions.get(x.id) !== x) put.push(x);
    }
    const del = [];
    for (const id of P.sessions.keys()) if (!seen.has(id)) del.push(id);
    const live = liveOf(s);
    return { doc, docChanged, put, del, live, liveChanged: live !== P.live };
  }

  async function flush() {
    if (!hydrated || frozen) return;
    if (!db()) return legacyFlush();
    if (busy) { again = true; return busy; }
    clearTimeout(timer); timer = null;
    const s = host.getState();
    const d = diff(s);
    if (!d.docChanged && !d.put.length && !d.del.length && !d.liveChanged) { scheduleMirror(s); return; }
    const D = db();
    if (!D) return;
    const myRev = ++rev;
    busy = (async () => {
      try {
        await D.transaction('rw', D.sessions, D.kv, async () => {
          if (d.put.length) await D.sessions.bulkPut(d.put);
          if (d.del.length) await D.sessions.bulkDelete(d.del);
          if (d.docChanged) await D.kv.put({ k: 'doc', rev: myRev, at: Date.now(), data: d.doc });
          if (d.liveChanged) await D.kv.put({ k: 'live', rev: myRev, at: Date.now(), data: d.live });
          await D.kv.put({ k: 'head', rev: myRev, at: Date.now(), v: STORAGE_V });
        });
        if (d.docChanged) P.doc = d.doc;
        for (const x of d.put) P.sessions.set(x.id, x);
        for (const id of d.del) P.sessions.delete(id);
        if (d.liveChanged) P.live = d.live;
        if (full) { full = false; host.onStorageFull && host.onStorageFull(false); }
        try { ls.set(IDB_FLAG, '1'); } catch (_) { /* ignore */ }
        post({ t: 'saved', tab: TAB, rev: myRev, doc: d.docChanged, put: d.put.map((x) => x.id), del: d.del, live: d.liveChanged });
        scheduleMirror(host.getState());
      } catch (e) {
        if (isQuota(e)) { full = true; host.onStorageFull && host.onStorageFull(true); }
        else console.warn('[liftlog] saving to IndexedDB failed', e);
        clearTimeout(timer); timer = setTimeout(() => { timer = null; flush(); }, 5000);
      }
    })();
    try { await busy; } finally {
      busy = null;
      if (again) { again = false; flush(); }
    }
  }

  // No IndexedDB (very old browsers, some private modes): the whole state goes to localStorage as before.
  function legacyFlush() {
    clearTimeout(timer); timer = null;
    const s = host.getState();
    try {
      const json = JSON.stringify(persistable(s));
      ls.set(LS_KEY, json);
      ls.set(SIG_KEY, JSON.stringify({ rev: ++rev, len: json.length, h: strHash(json), partial: false, at: Date.now() }));
      lastMirror = json;
      if (full) { full = false; host.onStorageFull && host.onStorageFull(false); }
    } catch (e) {
      if (isQuota(e)) { full = true; host.onStorageFull && host.onStorageFull(true); }
    }
  }

  // ── mirror (localStorage snapshot for the next start) ──
  let mirrorT = null;
  // During a live workout the mirror is only rewritten while that is cheap (a
  // small history, or a big one whose mirror is partial anyway): logging a set
  // never stringifies a large state. The live workout itself is in LIVE_KEY.
  function mirrorCheap(s) {
    let done = 0;
    for (const x of s.sessions || []) if (x && x.completedAt) done++;
    const est = done * avgLen;
    return est < LIVE_MIRROR_MAX || est >= MIRROR_MAX * 0.9;
  }
  function scheduleMirror(s) {
    if (mirrorBlocked || (liveOf(s) && !mirrorCheap(s))) return;
    clearTimeout(mirrorT);
    const run = () => { mirrorT = null; writeMirror(host.getState()); };
    mirrorT = setTimeout(() => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 1000 }); else run();
    }, 250);
  }
  function writeMirror(s, force = false) {
    if (!force && (mirrorBlocked || (liveOf(s) && !mirrorCheap(s)))) return;
    const cur = ls.get(LS_KEY);
    if (!force && cur != null && cur !== lastMirror) { mirrorBlocked = true; return; } // someone else wrote it: the next start imports it
    const p = persistable(s);
    const done = (p.sessions || []).filter((x) => x && x.completedAt).length;
    let partial = false, json = null;
    if (done * avgLen < MIRROR_MAX * 0.9) {
      json = JSON.stringify(p);
      if (done) avgLen = Math.max(500, json.length / done);
    }
    if (!json || json.length > MIRROR_MAX) {
      const keep = new Set((p.sessions || []).filter((x) => x && x.completedAt).sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0)).slice(0, MIRROR_RECENT).map((x) => x));
      json = JSON.stringify({ ...p, sessions: (p.sessions || []).filter((x) => !x.completedAt || keep.has(x)) });
      partial = true;
    }
    if (json.length > MIRROR_MAX) { dropMirror(); return; }
    try {
      ls.set(LS_KEY, json);
      ls.set(SIG_KEY, JSON.stringify({ rev, len: json.length, h: strHash(json), partial, at: Date.now() }));
      lastMirror = json;
    } catch (_) { dropMirror(); } // no room: the next start waits for IndexedDB instead of showing a stale copy
  }
  function dropMirror() { ls.del(LS_KEY); ls.del(SIG_KEY); lastMirror = null; }

  // ── other tabs ──
  async function onMessage(m) {
    if (!m || m.tab === TAB || m.t !== 'saved' || !hydrated) return;
    if (m.rev > rev) rev = m.rev;
    const D = db(); if (!D) return;
    try {
      const [doc, live, put] = await Promise.all([
        m.doc ? kvGet('doc') : null,
        m.live ? kvGet('live') : null,
        m.put && m.put.length ? D.sessions.bulkGet(m.put) : [],
      ]);
      const patch = { doc: doc ? doc.data : null, put: (put || []).filter(Boolean), del: m.del || [], hasLive: !!m.live, live: live ? live.data : null };
      host.onRemote && host.onRemote(patch);
    } catch (e) { console.warn('[liftlog] reading another tab’s changes failed', e); }
  }
  /** After the store applied another tab's patch: those records are on disk already. */
  function adopt(patch, next) {
    if (patch.doc) P.doc = docOf(next);
    for (const x of patch.put || []) { const cur = (next.sessions || []).find((y) => y.id === x.id); if (cur) P.sessions.set(x.id, cur); }
    for (const id of patch.del || []) P.sessions.delete(id);
    if (patch.hasLive) { P.live = liveOf(next); writeLiveKey(P.live); }
  }

  // ── hydrate: reconcile the boot snapshot with IndexedDB ──
  /**
   * boot = bootRead() result, state = the state rendered from it.
   * → { replace?: loadedState } when IndexedDB holds newer / more data than the boot snapshot.
   */
  async function hydrate(boot, state) {
    const D = db();
    if (!D) { hydrated = true; baseline(state); if (boot.source === 'mirror') lastMirror = boot.raw; return { unavailable: true }; }
    let got;
    try { got = await readAll({ sessions: false }); } catch (e) {
      console.warn('[liftlog] IndexedDB unreadable — saving to localStorage instead', e);
      _db = null; _noIdb = true;
      hydrated = true; baseline(state); if (boot.source === 'mirror') lastMirror = boot.raw;
      return { unavailable: true };
    }
    const idbRev = got.head ? got.head.rev || 0 : 0;
    rev = Math.max(rev, idbRev, boot.rev || 0);
    const persistNow = async (s, why) => {
      const r = ++rev;
      await writeAll(s, r);
      if (!(await verifyAll(s, r))) throw new Error('verification failed after ' + why);
    };
    if (boot.source === 'external' || boot.source === 'corrupt-recovered') {
      // One-time migration from localStorage, or a seeded / edited copy: it replaces IndexedDB.
      const first = !got.doc;
      if (!first) {
        try { const old = await readAll(); await kvPut('preImport', { at: Date.now(), data: { ...(old.doc && old.doc.data), sessions: [...(old.sessions || []), ...(old.live && old.live.data ? [old.live.data] : [])] } }); } catch (_) { /* best effort */ }
      }
      await persistNow(state, 'import');
      if (first && boot.raw) await kvPut('rollback.v1', { at: Date.now(), v: STORAGE_V, raw: boot.raw });
      hydrated = true; baseline(state);
      lastMirror = null; writeMirror(state, true); // our signature now (or dropped if too big)
      return { migrated: first };
    }
    if (!got.doc) {
      // Nothing in IndexedDB (first run of a fresh install, or the browser evicted it).
      if (boot.source === 'mirror' || (state.sessions || []).length || (state.programs || []).length) await persistNow(state, 'first write');
      hydrated = true; baseline(state);
      if (boot.source === 'mirror') lastMirror = boot.raw;
      return {};
    }
    if (boot.source === 'mirror') lastMirror = boot.raw;
    if (boot.source === 'mirror' && !boot.partial && boot.rev >= idbRev) {
      if (boot.rev > idbRev) await persistNow(state, 're-persist'); // IndexedDB fell behind the mirror
      hydrated = true; baseline(state);
      return {};
    }
    // IndexedDB has more (partial mirror, mirror behind, or no mirror): load it all.
    const all = await readAll();
    const loaded = { ...(all.doc.data || {}) };
    const sessions = all.sessions || [];
    const live = all.live && all.live.data;
    if (live && !sessions.some((x) => x.id === live.id)) sessions.push(live);
    loaded.sessions = sessions.sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
    return {
      replace: loaded,
      mirrorNewer: boot.source === 'mirror' && boot.rev > idbRev,
      // After the store committed the merged state: what is on disk is `loaded`; write the difference.
      finish: (finalState) => { hydrated = true; baseline(loaded); schedule(finalState); },
    };
  }

  return {
    schedule,
    flush,
    hydrate,
    adopt,
    baseline,
    isHydrated: () => hydrated,
    markHydrated: (s) => { hydrated = true; baseline(s); },
    flushSoon: () => { if (timer || busy) return flush(); return Promise.resolve(); },
    writeMirrorNow: () => writeMirror(host.getState()),
    rev: () => rev,
    freeze: () => { frozen = true; clearTimeout(timer); timer = null; clearTimeout(mirrorT); },
  };
}

// ───────────────────────────── undo restore ─────────────────────────────
//
// A JSON restore keeps what was on the device in IndexedDB ('preRestore').
// "Undo restore" is offered for UNDO_WINDOW_MS; it puts that state back but
// KEEPS every workout finished after the restore and the live workout. The
// copy is deleted when the window closes.

export const UNDO_WINDOW_MS = 24 * 3600e3;

export function undoInfo(now = Date.now()) {
  const u = parse(ls.get(UNDO_KEY));
  if (!u || !Number.isFinite(u.at)) return null;
  if (now - u.at > UNDO_WINDOW_MS) return { at: u.at, expired: true };
  return { at: u.at, until: u.at + UNDO_WINDOW_MS, sessions: u.sessions || 0 };
}
export async function savePreRestore(state) {
  const at = Date.now();
  const data = persistable(state);
  try {
    if (!db()) throw new Error('no IndexedDB');
    await kvPut('preRestore', { at, data });
    ls.set(UNDO_KEY, JSON.stringify({ at, sessions: (data.sessions || []).filter((x) => x && x.completedAt).length }));
    return true;
  } catch (_) { ls.del(UNDO_KEY); return false; }
}
export async function dropPreRestore() { ls.del(UNDO_KEY); try { await kvDel('preRestore'); } catch (_) { /* ignore */ } }
/** Start-up housekeeping: an expired undo copy, and the migration's rollback copy once the storage version moved on. */
export async function expirePreRestore() {
  const u = undoInfo();
  if (u && u.expired) await dropPreRestore();
  try { const rb = await kvGet('rollback.v1'); if (rb && (Number(rb.v) || 0) < STORAGE_V) await kvDel('rollback.v1'); } catch (_) { /* ignore */ }
}
export async function readPreRestore() {
  const u = undoInfo();
  if (!u || u.expired) return null;
  const r = await kvGet('preRestore');
  return r && r.data ? { at: r.at, data: r.data } : null;
}
/**
 * The state to go back to: the pre-restore copy plus every workout finished
 * after the restore (and the live one) from `cur`. Pure.
 */
export function mergeUndo(pre, cur, at) {
  const keep = (cur.sessions || []).filter((x) => x && ((x.completedAt && x.completedAt > at) || (!x.completedAt && x.id === cur.activeSessionId)));
  const ids = new Set(keep.map((x) => x.id));
  const sessions = [...(pre.sessions || []).filter((x) => x && !ids.has(x.id)), ...keep].sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
  const live = keep.find((x) => !x.completedAt);
  return { ...pre, sessions, activeSessionId: live ? live.id : null, kept: keep.filter((x) => x.completedAt).length };
}
/** Crash-screen path (no React): undo on disk, then the caller reloads. */
export async function undoRestoreOnDisk(rev = Date.now()) {
  const pre = await readPreRestore();
  if (!pre) return false;
  const all = await readAll();
  const cur = { ...(all.doc && all.doc.data), sessions: [...(all.sessions || []), ...(all.live && all.live.data ? [all.live.data] : [])] };
  const next = mergeUndo(pre.data, cur, pre.at);
  delete next.kept;
  next.syncRestore = { id: 'rs_' + Math.random().toString(36).slice(2, 8), asOf: 1 };
  await writeAll(next, rev);
  ls.del(LS_KEY); ls.del(SIG_KEY); ls.del(LIVE_KEY);
  await dropPreRestore();
  return true;
}

/** Test / dev seam: replace everything with `state` (any size). Reload afterwards. */
export async function seedState(state) {
  await writeAll(state, Date.now());
  ls.del(LS_KEY); ls.del(SIG_KEY); ls.del(LIVE_KEY);
  try { ls.set(IDB_FLAG, '1'); } catch (_) { /* ignore */ }
}

/** The live-workout key ({ id, at, session } or null). */
export function readLiveKey() { return parse(ls.get(LIVE_KEY)); }
